import React, { useEffect, useState, useRef } from 'react';
import { Button } from './Button';
import { ArrowLeftIcon, SendIcon, PaperclipIcon, PhoneIcon, VideoIcon, InfoIcon, ClockIcon, CalendarIcon } from 'lucide-react';
interface TherapistChatProps {
  onBack: () => void;
}
interface Message {
  id: string;
  sender: 'user' | 'therapist';
  text: string;
  timestamp: string;
  status?: 'sent' | 'delivered' | 'read';
  isNew?: boolean;
}
const TherapistChat: React.FC<TherapistChatProps> = ({
  onBack
}) => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    sender: 'therapist',
    text: 'Hallo Lisa, welkom bij Mosy. Ik ben Marieke, je persoonlijke therapeut. Hoe kan ik je vandaag helpen?',
    timestamp: 'Gisteren, 14:30'
  }, {
    id: '2',
    sender: 'user',
    text: 'Hoi Marieke, ik heb de laatste tijd moeite met slapen en voel me wat gespannen.',
    timestamp: 'Gisteren, 14:35',
    status: 'read'
  }, {
    id: '3',
    sender: 'therapist',
    text: 'Dat klinkt vervelend, Lisa. Slaapproblemen en spanning kunnen elkaar versterken. Kun je me wat meer vertellen over wat er speelt in je leven op dit moment?',
    timestamp: 'Gisteren, 14:40'
  }, {
    id: '4',
    sender: 'user',
    text: "Ik heb het druk op mijn werk en er spelen wat dingen in mijn relatie. Ik merk dat ik 's avonds moeilijk kan ontspannen, 's 'Gisteren, 14:45'",
    status: "read'"
  }, {
    id: '5',
    sender: 'therapist',
    text: 'Ik begrijp het. Het is belangrijk om een balans te vinden. Heb je al enkele oefeningen in de app geprobeerd? De ademhalingsoefeningen kunnen helpen bij het ontspannen voor het slapen gaan.',
    timestamp: 'Gisteren, 14:50'
  }, {
    id: '6',
    sender: 'therapist',
    text: 'Ook zou ik graag meer willen horen over wat er speelt in je relatie, als je daar open over wilt zijn. Hoe voel je je vandaag?',
    timestamp: 'Vandaag, 09:15',
    isNew: true
  }]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showScheduleOptions, setShowScheduleOptions] = useState(false);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleSendMessage = () => {
    if (inputValue.trim()) {
      const newMessage: Message = {
        id: Date.now().toString(),
        sender: 'user',
        text: inputValue.trim(),
        timestamp: 'Zojuist',
        status: 'sent'
      };
      setMessages([...messages, newMessage]);
      setInputValue('');
      // Simulate therapist response after a delay
      setTimeout(() => {
        const therapistResponse: Message = {
          id: (Date.now() + 1).toString(),
          sender: 'therapist',
          text: 'Dank voor je bericht. Ik begrijp je situatie. Zou je misschien een video gesprek willen plannen om dit verder te bespreken?',
          timestamp: 'Zojuist',
          isNew: true
        };
        setMessages(prevMessages => [...prevMessages, therapistResponse]);
      }, 3000);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  return <div className="w-full max-w-xl mx-auto py-8 h-full flex flex-col" data-id="element-1425">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-2" data-id="element-1426">
        <div className="flex items-center" data-id="element-1427">
          <button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-100" aria-label="Terug" data-id="element-1428">
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-1429" />
          </button>
          <div className="flex items-center" data-id="element-1430">
            <div className="w-10 h-10 rounded-full border-2 border-gray-300 bg-gray-100 flex items-center justify-center mr-3" data-id="element-1431">
              <span className="text-gray-700 font-medium" data-id="element-1432">M</span>
            </div>
            <div data-id="element-1433">
              <h1 className="text-lg font-medium text-gray-800" data-id="element-1434">
                Marieke de Vries
              </h1>
              <p className="text-xs text-gray-500 flex items-center" data-id="element-1435">
                <span className="w-2 h-2 rounded-full bg-gray-400 mr-1" data-id="element-1436"></span>
                Beschikbaar
              </p>
            </div>
          </div>
        </div>
        <div className="flex space-x-2" data-id="element-1437">
          <button className="p-2 rounded-full hover:bg-gray-100" onClick={() => alert('Video gesprek functie komt binnenkort beschikbaar')} aria-label="Video gesprek" data-id="element-1438">
            <VideoIcon className="w-5 h-5 text-gray-600" data-id="element-1439" />
          </button>
          <button className="p-2 rounded-full hover:bg-gray-100" onClick={() => alert('Bel functie komt binnenkort beschikbaar')} aria-label="Bellen" data-id="element-1440">
            <PhoneIcon className="w-5 h-5 text-gray-600" data-id="element-1441" />
          </button>
          <button className="p-2 rounded-full hover:bg-gray-100" onClick={() => alert('Therapeut informatie komt binnenkort beschikbaar')} aria-label="Informatie" data-id="element-1442">
            <InfoIcon className="w-5 h-5 text-gray-600" data-id="element-1443" />
          </button>
        </div>
      </div>

      {/* Therapist info card */}
      <div className="px-2 mb-6" data-id="element-1444">
        <div className="p-4 border-2 border-gray-300 rounded-xl bg-gray-50" data-id="element-1445">
          <div className="flex items-center justify-between mb-2" data-id="element-1446">
            <h2 className="text-sm font-medium text-gray-800" data-id="element-1447">
              Marieke de Vries
            </h2>
            <span className="text-xs text-gray-500" data-id="element-1448">GZ-Psycholoog</span>
          </div>
          <p className="text-xs text-gray-600 mb-3" data-id="element-1449">
            Specialisaties: angst, stress, slaapproblemen, relaties
          </p>
          <div className="flex justify-between" data-id="element-1450">
            <button className="text-xs text-gray-700 border border-gray-300 rounded-lg px-3 py-1 flex items-center bg-white hover:bg-gray-50" onClick={() => setShowScheduleOptions(!showScheduleOptions)} data-id="element-1451">
              <CalendarIcon className="w-3 h-3 mr-1" data-id="element-1452" />
              Afspraak plannen
            </button>
            <button className="text-xs text-gray-700 border border-gray-300 rounded-lg px-3 py-1 flex items-center bg-white hover:bg-gray-50" onClick={() => alert('Profiel bekijken functie komt binnenkort beschikbaar')} data-id="element-1453">
              Profiel bekijken
            </button>
          </div>
        </div>
      </div>

      {/* Schedule options */}
      {showScheduleOptions && <div className="px-2 mb-6" data-id="element-1454">
          <div className="p-4 border-2 border-gray-300 rounded-xl bg-white" data-id="element-1455">
            <h3 className="text-sm font-medium text-gray-800 mb-3 flex items-center" data-id="element-1456">
              <ClockIcon className="w-4 h-4 mr-1" data-id="element-1457" />
              Beschikbare momenten
            </h3>
            <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-1458">
              <button className="p-2 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50" data-id="element-1459">
                Vandaag, 15:00
              </button>
              <button className="p-2 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50" data-id="element-1460">
                Vandaag, 16:30
              </button>
              <button className="p-2 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50" data-id="element-1461">
                Morgen, 10:00
              </button>
              <button className="p-2 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50" data-id="element-1462">
                Morgen, 14:00
              </button>
            </div>
            <button className="text-xs text-gray-700 hover:text-gray-900" onClick={() => alert('Meer tijden bekijken functie komt binnenkort beschikbaar')} data-id="element-1463">
              Meer tijden bekijken
            </button>
          </div>
        </div>}

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto mb-4 space-y-4 px-2" data-id="element-1464">
        <div className="text-center" data-id="element-1465">
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full" data-id="element-1466">
            Conversatie gestart op 15 maart 2023
          </span>
        </div>
        {messages.map(message => <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1467">
            <div className={`max-w-[80%] p-4 rounded-xl ${message.sender === 'user' ? 'bg-gray-100 rounded-tr-none' : 'border-2 border-gray-300 rounded-tl-none'} ${message.isNew ? 'animate-fadeIn' : ''}`} data-id="element-1468">
              <p className="text-sm text-gray-800 mb-1" data-id="element-1469">{message.text}</p>
              <div className="flex items-center justify-end" data-id="element-1470">
                <span className="text-xs text-gray-500" data-id="element-1471">
                  {message.timestamp}
                </span>
                {message.sender === 'user' && message.status && <span className="ml-1 text-xs text-gray-400" data-id="element-1472">
                    {message.status === 'sent' && '✓'}
                    {message.status === 'delivered' && '✓✓'}
                    {message.status === 'read' && <span className="text-gray-600" data-id="element-1473">✓✓</span>}
                  </span>}
              </div>
            </div>
          </div>)}
        <div ref={messagesEndRef} data-id="element-1474" />
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 pt-4 px-2" data-id="element-1475">
        <div className="flex items-end border-2 border-gray-300 rounded-xl overflow-hidden p-2" data-id="element-1476">
          <textarea placeholder="Typ een bericht..." value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} className="flex-1 resize-none border-0 focus:ring-0 focus:outline-none text-sm max-h-32 min-h-[40px]" rows={1} data-id="element-1477" />
          <div className="flex items-center space-x-2 ml-2" data-id="element-1478">
            <button className="p-2 rounded-full hover:bg-gray-100" aria-label="Bijlage toevoegen" onClick={() => alert('Bijlage toevoegen functie komt binnenkort beschikbaar')} data-id="element-1479">
              <PaperclipIcon className="w-5 h-5 text-gray-600" data-id="element-1480" />
            </button>
            <button className={`p-2 rounded-full ${inputValue.trim() ? 'bg-gray-200 hover:bg-gray-300' : 'text-gray-400'}`} onClick={handleSendMessage} disabled={!inputValue.trim()} aria-label="Verstuur bericht" data-id="element-1481">
              <SendIcon className="w-5 h-5" data-id="element-1482" />
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-1483">
          Berichten worden end-to-end versleuteld en alleen gedeeld met je
          therapeut
        </p>
      </div>
    </div>;
};
export default TherapistChat;